import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
	UncontrolledDropdown,
} from 'reactstrap';

import { messages } from '../../../../../../_shared/i18n/messages';

import { CONTINUATION_TYPES } from '../../../../../constants';
import { useFormEditorContext } from '../../../contexts/FormEditorContext';
import { SECTION_EDITOR_FIELDS } from '../../../hooks/useFormEditorData';
import { formSectionDataShape } from '../../../utils/propTypes';

import './FormNavigationOptionsDropdown.scss';

export const FORM_ITEM_TYPE_SECTION = 'section';
export const FORM_ITEM_TYPE_OPTION = 'option';
export const FORM_ITEM_TYPE_ADVANCED = 'advanced-setting';

const propTypes = {

	/**
	 * The list of form sections.
	 */
	sections: PropTypes.arrayOf(formSectionDataShape).isRequired,

	/**
	 * The form item (section, field option, or advanced setting).
	 */
	formItem: PropTypes.shape({

		/**
		 * The form item ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The type of conditional navigation setting for the form item.
		 */
		continuation_type: PropTypes.oneOf([
			CONTINUATION_TYPES.SECTION,
			CONTINUATION_TYPES.DEFAULT,
			CONTINUATION_TYPES.SUBMIT,
			CONTINUATION_TYPES.PRODUCT_TICKETS,
			CONTINUATION_TYPES.PRODUCT_TICKET_GROUPS,
		]),

		/**
		 * The ID of the proceeding form section if the form item condition is met.
		 */
		continuation_section_id: PropTypes.string,

	}).isRequired,

	/**
	 * The type of form item.
	 */
	formItemType: PropTypes.oneOf([
		FORM_ITEM_TYPE_SECTION,
		FORM_ITEM_TYPE_OPTION,
		FORM_ITEM_TYPE_ADVANCED
	]).isRequired,

	/**
	 * Flag indicating if advanced option should be down in navigation
	 * settings dropdown.
	 */
	showAdvancedOption: PropTypes.bool,

	/**
	 * Callback function to update the form item state.
	 */
	onChange: PropTypes.func.isRequired,

	/**
	 * Callback function to toggle the advanced settings modal for sections.
	 */
	onToggleAdvancedModal: PropTypes.func,

};

/**
 * Renders a dropdown component that allows a user to select
 * the navigation option for the form section or form field option.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object} The component to render.
 */
const FormNavigationOptionsDropdown = (props) => {

	const {
		formItem,
		formItemType,
		sections,
		showAdvancedOption,
		onChange,
		onToggleAdvancedModal
	} = props;

	const intl = useIntl();

	const { defaultLanguage } = useFormEditorContext();

	/**
	 * Updates the navigation settings for the form item given
	 * change to navigation options dropdown.
	 *
	 * @param {Object} navOption             The selected navigation option.
	 * @param {Object} navOption.section_id  The section ID for the navigation option.
	 * @param {Object} navOption.type        The continuation type for the navigation option.
	 */
	const handleOnSelect = (navOption) => {

		if (navOption.type === CONTINUATION_TYPES.DEFAULT ||
			navOption.type === CONTINUATION_TYPES.SUBMIT) {

			onChange(formItem.id, 'continuation_type', navOption.type);
			onChange(formItem.id, 'continuation_section_id', null);

			return;

		}

		onChange(formItem.id, 'continuation_type', navOption.type);
		onChange(formItem.id, 'continuation_section_id', navOption.section_id);

	};

	// Build out conditional navigation options for the form item
	const navigationOptions = sections
		.filter((section) => {

			// Remove the current section
			if (formItemType === FORM_ITEM_TYPE_SECTION &&
				formItem.id === section.id) {
				return false;
			}

			return true;

		}).map((section) => {

			// Get the form section content data for the default language
			const activeTranslation = section[SECTION_EDITOR_FIELDS.CONTENTS].find(
				(content) => content.language === defaultLanguage
			);

			const idx = sections.findIndex((temp) => temp.id === section.id);

			return {
				key: `${formItemType}-${formItem.id}-section-${section.id}-navigation-option`,
				section_id: section.id,
				title: intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_NAVIGATION_OPTIONS_SECTION, {
					number: idx + 1,
					title: activeTranslation?.title || '',
				}),
				type: CONTINUATION_TYPES.SECTION,
				onClick: (option) => handleOnSelect(option),
			};

		});

	// Insert default option at beginning of list
	navigationOptions.unshift({
		key: `${formItemType}-${formItem.id}-default-navigation-option`,
		title: intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_NAVIGATION_OPTIONS_DEFAULT),
		type: CONTINUATION_TYPES.DEFAULT,
		onClick: (option) => handleOnSelect(option),
	});

	// Insert submit option at end of list
	navigationOptions.push({
		key: `${formItemType}-${formItem.id}-submit-navigation-option`,
		title: intl.formatMessage(messages.TC_COMPONENTS_GENERAL_SUBMIT_LABEL),
		type: CONTINUATION_TYPES.SUBMIT,
		onClick: (option) => handleOnSelect(option),
	});

	// Insert advanced option for form sections only
	if (showAdvancedOption && formItemType === FORM_ITEM_TYPE_SECTION) {
		navigationOptions.push({
			key: `${formItemType}-${formItem.id}-advanced-navigation-option`,
			type: FORM_ITEM_TYPE_ADVANCED,
			title: `${intl.formatMessage(messages.TC_COMPONENTS_GENERAL_ADVANCED_LABEL)}...`,
			onClick: onToggleAdvancedModal,
		});
	}

	// Determine currently selected option from the form item
	let selectedNavOption = formItem &&
		navigationOptions.find((navOption) => {

			if (formItem.continuation_type === CONTINUATION_TYPES.PRODUCT_TICKETS ||
				formItem.continuation_type === CONTINUATION_TYPES.PRODUCT_TICKET_GROUPS) {
				return navOption.type === FORM_ITEM_TYPE_ADVANCED;
			}

			if (navOption.type === CONTINUATION_TYPES.SECTION) {
				return navOption.section_id === formItem.continuation_section_id;
			}

			return navOption.type === formItem.continuation_type;

		});

	// Default to first option if none is currently selected
	if (!selectedNavOption) {
		selectedNavOption = navigationOptions[0];
	}

	return (
		<UncontrolledDropdown className={`navigation-options-dropdown ${formItemType}`}>
			<DropdownToggle
				className="nav-dropdown-toggle"
				title={selectedNavOption.title}
				color="neutral">
				<span className="nav-dropdown-toggle-label">
					{selectedNavOption.title}
				</span>
				<span>
					<i
						className="fas fa-caret-down ms-auto"
						aria-hidden="true" />
				</span>
			</DropdownToggle>
			<DropdownMenu end={formItemType === FORM_ITEM_TYPE_OPTION}>
				{navigationOptions.map((option) => {
					return (
						<DropdownItem
							key={option.key}
							className="nav-dropdown-item"
							title={option.title}
							onClick={() => option.onClick(option)}>
							{option.title}
						</DropdownItem>
					);
				})}
			</DropdownMenu>
		</UncontrolledDropdown>
	);

};

FormNavigationOptionsDropdown.propTypes = propTypes;

export default FormNavigationOptionsDropdown;
